import { type SupabaseClient, createClient } from "@supabase/supabase-js";
import { getIsPopupAuthCallback } from "./popupAuth.js";
import { acquireAuthLockWithoutHanging } from "./supabaseAuthLock.js";

// The Supabase JS client is used only for auth here, so it doesn't need the
// database schema generic.
type AlpharunSupabaseClient = SupabaseClient;

let supabase: AlpharunSupabaseClient | null;

export function getSupabaseClient(): AlpharunSupabaseClient {
  if (!supabase) {
    supabase = createSupabaseClient();
  }

  return supabase;
}

function createSupabaseClient(): AlpharunSupabaseClient {
  return createClient(
    import.meta.env.VITE_SUPABASE_APP_URL,
    import.meta.env.VITE_SUPABASE_PUBLIC_KEY,
    {
      auth: {
        // "pkce" is generally recommended vs "implicit" flow because the access/refresh tokens are not exposed to the client.
        // Instead, the client gets a token it uses to exchange for a session. See https://supabase.com/docs/guides/auth/sessions/pkce-flow
        flowType: getSupabaseAuthFlowType(),
        // Disable auto-detection if we are doing popup auth flow. Otherwise the popup will be authenticated automatically, not the parent window.
        detectSessionInUrl: !getIsPopupAuthCallback(),
        // Cap the auth storage lock's indefinite wait so a frozen tab holding the
        // Web Lock can't strand this tab on the loading screen forever.
        lock: acquireAuthLockWithoutHanging,
      },
    },
  );
}

/**
 * Normal SSO flows (Google, Microsoft, Okta,...) are "pkce" type (they use a code as result
 * of the authentication that we then exchange for a session).
 * Fountain SSO flow is different: they target our Supabase ACS endpoint with a SAML response
 * and a RelayState (the URL they want the user to be redirected to). In return, Supabase, if
 * authentication is successful, redirects the user to the specified RelayState and attaches
 * the authentication result (access token and refresh token) as a fragment URL. And that is a "implicit" flow type.
 *
 * So if we detect we are in the Fountain case, we set up our Supabase auth client with a "implicit" flow type
 * otherwise we default to "pkce".
 *
 * The same fragment is how `pnpm signin:local` and the container's
 * `pnpm signin:container` sign a developer or agent in, so container mode
 * depends on this detection.
 */
function getSupabaseAuthFlowType() {
  // Get the URL hash (everything after the # symbol)
  const hash = window.location.hash.substring(1);
  if (hash !== "") {
    // Parse the hash into key-value pairs
    const params = new URLSearchParams(hash);

    // Extract the tokens
    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");
    if (accessToken && refreshToken) {
      return "implicit";
    }
  }

  return "pkce";
}
